import { format, formatDate, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { moneyFormat } from 'helpers/moneyFormat';
import { useApp } from 'hooks/app';
import useTableOrder from 'hooks/tableOrder';
import { FormEvent, useMemo, useState } from 'react';
import { api } from 'services/api';
import { Surgerie } from 'types/surgerie';

export interface ReportSurgerieQueryParams {
  initial_date: Date;
  final_date: Date;
  patient_name: string;
  doctor_name: string;
  hospital_name: string;
  status: string;
  date_filter: 'created_at' | 'closed_at';
}

export function useFetchReportSurgeries() {
  const [, sort] = useTableOrder();
  const { isOpenedMenu, handleOpenMenu } = useApp();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedSurgerie, setSelectedSurgerie] = useState<Surgerie | null>(null);
  const [surgeries, setSurgeries] = useState<Surgerie[]>([]);
  const [filter, setFilter] = useState<ReportSurgerieQueryParams>({
    initial_date: startOfMonth(new Date()),
    final_date: new Date(),
    patient_name: '',
    doctor_name: '',
    hospital_name: '',
    status: 'closed',
    date_filter: 'created_at',
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);

  function handleChange(index: keyof ReportSurgerieQueryParams, value: any) {
    setFilter({
      ...filter,
      [index]: value,
    });
  }

  const formatSurgerieData = useMemo(
    () =>
      (surgeries: Surgerie[]): Surgerie[] => {
        return surgeries.map(surgerie => ({
          ...surgerie,

          formattedAnesthetistValue: moneyFormat(surgerie.value_anesthetist || 0),
          formattedDoctorValue: moneyFormat(surgerie.value_doctor || 0),
          formattedHospitalValue: moneyFormat(surgerie.value_hospital || 0),
          formattedPackagesValue: moneyFormat(surgerie.value_packages || 0),
          formattedBudgetValue: moneyFormat(surgerie.value_budget || 0),
          formattedTotalValue: moneyFormat(surgerie.value_total || 0),
          formattedMarkupValue: moneyFormat(surgerie.value_markup || 0),
          formattedValueEntry: moneyFormat(surgerie.value_entry || 0),
          formattedValuePaid: moneyFormat(surgerie.value_paid || 0),
          closed_at: surgerie.closed_at || '',
          formattedClosedAt: surgerie.closed_at ? formatDate(surgerie.closed_at, 'P', { locale: ptBR }) : '',
          formattedCreatedAt: formatDate(surgerie.created_at || new Date(), 'P', { locale: ptBR }),
          formattedDoctorName: surgerie.doctor?.doctor?.name || 'Não informado',
          formattedPatientName: surgerie.patient?.name || 'Não informado',
          formattedProcedureName: surgerie.procedure?.name || 'Não informado',
          formattedHospitalName: surgerie.hospital?.company_name || 'Não informado',
          formattedStatus: surgerie.status === 'open' ? 'Aberta' : 'Fechada',
        }));
      },
    [ptBR],
  );

  function fetchData(nextFilter = filter) {
    setLoading(true);
    if (isOpenedMenu) handleOpenMenu();

    api
      .get<Surgerie[]>('/budgets/all', {
        params: {
          ...nextFilter,
        },
      })
      .then(response => {
        const data = formatSurgerieData(response.data);

        setTotal(data.length);
        setSurgeries(data);
      })
      .catch(() => console.error('Erro ao buscar guias'))
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
  }

  function handleSort(index: string) {
    const p = sort(index, surgeries);
    setSurgeries(p);
  }

  return {
    filter,
    handleChange,
    loading,
    handleSearch,
    page,
    rows,
    total,
    handleSort,
    surgeries,
    anchorEl,
    setAnchorEl,
    selectedSurgerie,
    setSelectedSurgerie,
    setPage: handleChangePage,
    setRows: handleChangeRows,
  };
}
